"use client";

import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { zodResolver } from "../../../../forms/safe-zod-resolver";
import { Button } from "../../../button";
import { Input } from "../../../input";
import { Switch } from "../../../switch";
import { Textarea } from "../../../textarea";
import { Label } from "../../../label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../form";
import { EventSchema } from "../validation";
import type { EventFormValues } from "../types";
import {
  EventFormGrid,
  EventFormPrimaryColumn,
  EventFormSection,
  EventFormSidebar,
} from "../shared/EventFormShell";
import { RHFAttendeeFields } from "../shared/RHFAttendeeFields";
import { RHFDateTimeFields } from "../shared/RHFDateTimeFields";
import { Send, Loader2, Video } from "lucide-react";

/**
 * react-hook-form values for the Microsoft create form. Mirrors `EventSchema`
 * (subject/content/location/attendees) but allows `Date | null` for the date
 * pair so the form can carry an empty initial state; the resolver enforces the
 * non-null `z.date()` on submit. `isOnline` is NOT a form value — it stays a
 * prop-driven state owned by the modal.
 */
type MicrosoftFormValues = Omit<
  z.infer<typeof EventSchema>,
  "startDateTime" | "endDateTime"
> & {
  startDateTime: Date | null;
  endDateTime: Date | null;
};

interface EventFormProps {
  initialValues: EventFormValues;
  onSubmit: (values: EventFormValues) => Promise<void>;
  loading: boolean;
  formKey?: string;
  isOnline: boolean;
  setIsOnline: (isOnline: boolean) => void;
}

/** Fields persisted to localStorage (everything except the date pair). */
type PersistedFields = Omit<
  MicrosoftFormValues,
  "startDateTime" | "endDateTime"
>;

function toDate(value: Date | null | string): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function loadPersisted(formKey: string): Partial<PersistedFields> | null {
  try {
    const saved = localStorage.getItem(formKey);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as Partial<PersistedFields>;
    return parsed;
  } catch {
    return null;
  }
}

const EventForm = ({
  initialValues,
  onSubmit,
  loading,
  formKey = "microsoft-event-form" + initialValues.subject,
  isOnline,
  setIsOnline,
}: EventFormProps) => {
  const persisted = loadPersisted(formKey);

  const form = useForm<MicrosoftFormValues>({
    resolver: zodResolver<MicrosoftFormValues>(EventSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      subject: persisted?.subject ?? initialValues.subject ?? "",
      content: persisted?.content ?? initialValues.content ?? "",
      location: persisted?.location ?? initialValues.location ?? "",
      startDateTime: toDate(initialValues.startDateTime),
      endDateTime: toDate(initialValues.endDateTime),
      attendees:
        persisted?.attendees ??
        (initialValues.attendees?.length
          ? initialValues.attendees
          : [{ emailAddress: "" }]),
    },
  });

  // Persist every field except the date pair, keyed by formKey.
  const persistKeyRef = useRef(formKey);
  persistKeyRef.current = formKey;
  useEffect(() => {
    const subscription = form.watch((values) => {
      try {
        const toPersist: Partial<PersistedFields> = {
          subject: values.subject,
          content: values.content,
          location: values.location,
          attendees: (values.attendees ?? []).filter(
            (a): a is { emailAddress: string } => Boolean(a),
          ),
        };
        localStorage.setItem(persistKeyRef.current, JSON.stringify(toPersist));
      } catch {
        // ignore persistence failures
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleOnlineToggle = (checked: boolean) => {
    setIsOnline(checked);
    form.setValue("location", checked ? "ONLINE" : "", {
      shouldValidate: true,
    });
  };

  const onValid = async (values: MicrosoftFormValues) => {
    // Build the consumer's EventFormValues shape explicitly. The resolver has
    // validated the date pair as non-null `z.date()`, and `location`/`attendees`
    // always carry a value here. `isOnline` is the form's prop-driven state; the
    // modal re-merges the same value before invoking the consumer's onSubmit.
    const submitValues: EventFormValues = {
      subject: values.subject,
      content: values.content,
      location: values.location ?? "",
      startDateTime: values.startDateTime,
      endDateTime: values.endDateTime,
      attendees: values.attendees ?? [],
      isOnline,
    };
    try {
      await onSubmit(submitValues);
      try {
        localStorage.removeItem(persistKeyRef.current);
      } catch {
        // ignore
      }
    } catch {
      // caller handles submission errors
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onValid)} className="space-y-6">
        <EventFormGrid>
          <EventFormPrimaryColumn>
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Subject" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body Content</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={6}
                      placeholder="Describe the purpose or key points for the event."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={isOnline ? "Online Meeting" : "Add a location."}
                      disabled={isOnline}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </EventFormPrimaryColumn>

          <EventFormSidebar>
            <EventFormSection title="When">
              <RHFDateTimeFields
                control={form.control}
                startName="startDateTime"
                endName="endDateTime"
                stacked
              />
            </EventFormSection>

            <EventFormSection title="Meeting type" icon={Video}>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="isOnline" className="text-sm font-medium">
                  Online Meeting
                </Label>
                <Switch
                  id="isOnline"
                  checked={isOnline}
                  onCheckedChange={handleOnlineToggle}
                />
              </div>
            </EventFormSection>

            <EventFormSection title="Attendees">
              <RHFAttendeeFields
                control={form.control}
                name="attendees"
                emailKey="emailAddress"
              />
            </EventFormSection>
          </EventFormSidebar>
        </EventFormGrid>

        <Button
          type="submit"
          variant="outline"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Submit
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default EventForm;
